import { reckonBallEvents } from '@/content/reckon-ball';
import { galleryBySlug } from '@/data/adventureGalleries';
import { experienceEntityById } from '@/data/reckon-way/entities';
import { reckonWayEvents } from '@/data/reckon-way/events';
import {
  eventEntityRoute,
  eventSectionItems,
  resolveEventEntityDetails,
} from '@/lib/reckon-way/entityDetails';
import { traditions } from './traditions';

const eateryLookup = new Map();

const normalizeName = (value = '') => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

const michelinStarsByEatery = {
  'ha-restaurant': 1,
  'rose-s-luxury': 1,
  'the-inn-at-little-washington': 3,
};

const eateryTypeOverrides = {
  'fortune-of-war-hotel': 'Bar & Pub',
};

const labelForType = (name, description = '') => {
  const override = eateryTypeOverrides[normalizeName(name)];
  if (override) return override;
  const value = `${name} ${description}`.toLowerCase();
  if (/coffee|cafe|café/.test(value)) return 'Cafe';
  if (/brewery|brewing|beerhall|taproom/.test(value)) return 'Brewery';
  if (/pub|bar|tavern|public house/.test(value)) return 'Bar & Pub';
  if (/steakhouse|steak/.test(value)) return 'Steakhouse';
  return 'Restaurant';
};

const destinationForEvent = (event, entity) => {
  if (event.slug === 'aws-reinvent-las-vegas') return 'Las Vegas';
  if (event.slug === 'sxsw-london-2025') return entity?.city || 'London';
  if (event.slug === 'sxsw-sydney-2025') return 'Sydney';
  return event.destination || event.city || event.title;
};

const eventLandingRoute = (event) => (event.slug === 'aws-reinvent-las-vegas'
  ? { name: 'aws-reinvent', hash: '#dining' }
  : { name: 'reckon-way-gallery', params: { gallerySlug: event.slug }, hash: '#dining' });

const locationFor = (...values) => values.filter(Boolean)
  .filter((value, index, items) => items.indexOf(value) === index)
  .join(', ');

const firstImage = (details) => details.media
  .find((asset) => asset.type !== 'video' && (asset.src || asset.image))?.src || '';

const addEatery = ({
  name,
  description,
  image,
  location,
  destination,
  years = [],
  visitCount = 1,
  journey,
  href,
  hasNarrative = false,
  type,
}) => {
  if (!name) return;
  const id = normalizeName(name);
  const current = eateryLookup.get(id) || {
    id,
    slug: id,
    name,
    description: '',
    image: '',
    locations: new Set(),
    destinations: new Set(),
    years: new Set(),
    journeys: new Set(),
    visitCount: 0,
    href,
    hasNarrative: false,
    michelinStars: michelinStarsByEatery[id] || 0,
    type: type || labelForType(name, description),
  };

  if (!current.description && description) current.description = description;
  if (!current.image && image) current.image = image;
  if (location) current.locations.add(location);
  if (destination) current.destinations.add(destination);
  years.filter(Boolean).forEach((year) => current.years.add(Number(year)));
  if (journey) current.journeys.add(journey);
  current.visitCount += visitCount;

  if (hasNarrative && !current.hasNarrative) {
    current.href = href;
    current.hasNarrative = true;
  }

  eateryLookup.set(id, current);
};

[
  {
    name: "Joe's Stone Crab",
    description: 'A South Beach dinner that opened Reckon’s first corporate retreat before the crew boarded the Norwegian Sky.',
    image: galleryBySlug['bahamas-2024']?.heroImage,
    location: 'Miami Beach, Florida',
    destination: 'Miami',
    years: [2024],
    journey: 'Miami & Bahamas',
    href: { name: 'reckon-way-gallery', params: { gallerySlug: 'bahamas-2024' } },
    type: 'Restaurant',
  },
  {
    name: 'HA’ Restaurant',
    description: 'The closing dinner after a yacht outing on the final night of the Xcaret corporate retreat.',
    image: galleryBySlug['xcaret-2025']?.heroImage,
    location: 'Riviera Maya, Mexico',
    destination: 'Riviera Maya',
    years: [2025],
    journey: 'Xcaret Mexico 2025',
    href: { name: 'reckon-way-gallery', params: { gallerySlug: 'xcaret-2025' } },
    type: 'Restaurant',
  },
].forEach((eatery) => addEatery(eatery));

[
  {
    eventSlug: 'sxsw-london-2025', entityId: 'guinness-storehouse-tour', name: 'Guinness Storehouse', type: 'Brewery',
  },
  {
    eventSlug: 'sxsw-london-2025', entityId: 'temple-bar-dublin', name: 'The Temple Bar', type: 'Bar & Pub',
  },
].forEach((placeholder) => {
  const event = reckonWayEvents.find(({ slug }) => slug === placeholder.eventSlug);
  const item = eventSectionItems(event, 'activities')
    .find(({ entityId }) => entityId === placeholder.entityId);
  const entity = experienceEntityById[placeholder.entityId];
  if (!event || !item || !entity) return;
  const details = resolveEventEntityDetails({
    event, sectionKey: 'activities', item, entity,
  });
  addEatery({
    name: placeholder.name,
    description: details.description,
    image: firstImage(details),
    location: locationFor(entity.city, entity.country),
    destination: entity.city,
    years: [event.year],
    journey: event.title,
    href: eventEntityRoute(event, 'activities', item),
    type: placeholder.type,
  });
});

reckonWayEvents.forEach((event) => {
  eventSectionItems(event, 'dining').forEach((item) => {
    const entity = experienceEntityById[item.entityId] || null;
    const details = resolveEventEntityDetails({
      event, sectionKey: 'dining', item, entity,
    });
    const hasNarrative = Boolean(details.storySections.length || details.storyPages.length);
    const hasDetailPage = Boolean(
      hasNarrative || details.storyItems.length || details.media.length,
    );
    const years = item.years?.length ? item.years : [item.year || event.year];
    addEatery({
      name: details.title,
      description: details.description,
      image: firstImage(details),
      location: details.location || locationFor(
        entity?.city || event.city,
        entity?.region || event.region,
        entity?.country || event.country,
      ),
      destination: destinationForEvent(event, entity),
      years,
      visitCount: item.visitCount || years.length || 1,
      journey: event.title,
      href: hasDetailPage
        ? eventEntityRoute(event, 'dining', item)
        : eventLandingRoute(event),
      hasNarrative,
      type: labelForType(details.title, details.description),
    });
  });
});

reckonBallEvents.forEach((event) => {
  const { dining } = event;
  addEatery({
    name: dining.restaurant,
    description: dining.summary,
    image: dining.images?.[0]?.src || dining.image || event.coverImage || event.hero.image,
    location: dining.location,
    destination: 'Local',
    years: [event.year],
    visitCount: 1,
    journey: `Reckon Ball ${event.year}`,
    href: { name: 'reckon-ball-year', params: { year: String(event.year) } },
    hasNarrative: true,
    type: labelForType(dining.restaurant, dining.summary),
  });
});

traditions.forEach((tradition) => {
  if (tradition.archiveRouteName) return;
  const traditionRoute = {
    name: 'reckon-way-tradition',
    params: { traditionSlug: tradition.slug },
  };
  const placeRoute = (place) => ({
    name: 'reckon-way-place',
    params: { placeSlug: place.slug || normalizeName(place.name) },
  });
  const usesTraditionNarrative = ['chick-fil-a-friday', 'end-of-year-lunch']
    .includes(tradition.id);
  const addTraditionPlace = (place, visitCount = 1, years = []) => {
    if (!place?.name) return;
    addEatery({
      name: place.name,
      description: place.description || tradition.summary,
      image: place.image || tradition.heroImage,
      location: locationFor(place.city, place.state, place.country),
      destination: 'Local',
      years,
      visitCount,
      journey: tradition.name,
      href: usesTraditionNarrative ? traditionRoute : placeRoute(place),
      hasNarrative: Boolean(
        place.storySections?.length
        || (usesTraditionNarrative && tradition.storySections?.length),
      ),
      type: labelForType(place.name, place.description),
    });
  };

  const occurrences = tradition.occurrences || [];
  occurrences.forEach((occurrence) => addTraditionPlace(
    occurrence.place,
    1,
    [occurrence.date?.slice(0, 4)],
  ));

  const occurrencePlaceIds = new Set(occurrences.map((occurrence) => (
    normalizeName(occurrence.place?.name)
  )));
  if (tradition.defaultPlace && !occurrencePlaceIds.has(normalizeName(tradition.defaultPlace.name))) {
    addTraditionPlace(
      tradition.defaultPlace,
      tradition.occurrenceCount || 1,
      [tradition.startDate?.slice(0, 4)],
    );
  } else if (tradition.defaultPlace) {
    addTraditionPlace(tradition.defaultPlace, 0);
  }

  (tradition.locations || []).forEach((place) => {
    if (!occurrencePlaceIds.has(normalizeName(place.name))) addTraditionPlace(place);
  });
});

export const eats = [...eateryLookup.values()]
  .map((eatery) => ({
    ...eatery,
    locations: [...eatery.locations].sort(),
    destinations: [...eatery.destinations].sort(),
    years: [...eatery.years].sort((a, b) => b - a),
    journeys: [...eatery.journeys].sort(),
    visitCount: Math.max(eatery.visitCount, eatery.years.size, 1),
  }))
  .sort((left, right) => (
    Number(right.hasNarrative) - Number(left.hasNarrative)
    || left.name.localeCompare(right.name)
  ));

export const eatsBySlug = eats.reduce((lookup, eatery) => ({
  ...lookup,
  [eatery.slug]: eatery,
}), {});

export default eats;
